import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Breadcrumb, Button, Card, Divider, Empty, Popconfirm, Table, Tabs, Tag } from "antd";
import { useDispatch } from "react-redux";
import intranetService from "../../services/intranet-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import { createdColumnStatic, withCreatedColumn } from '../../shared/functional/created-sort';
import AddEditIntranetCardDialog from "./AddEditIntranetCardDialog";
import ManageWorkspaceTabsDialog, { tabLabel } from "./ManageWorkspaceTabsDialog";
import { INTRANET_MODULES, LINK_TYPE } from "../../constants/app-constants";
import { IntranetIcon } from "../../constants/intranet-icons";

/**
 * NMU-478: manage one sub-module of the staff-intranet portal.
 *
 * The three sub-modules in the sidebar (Workspace & Shortcuts, HR &
 * Administration, Operational Resources) all render this component with a
 * different `moduleKey` — every card in the portal has the same shape, so three
 * separate pages would be three copies that drift apart. The Workspace module
 * has more than one section, which is what the tabs are for.
 */
const IntranetManagement = ({ moduleKey }) => {
  const dispatch = useDispatch();
  const moduleDef = INTRANET_MODULES[moduleKey];
  /*
    NMU-601: Workspace & Shortcuts has its tabs in the database, managed under
    จัดการแท็บ; the other sub-modules keep their fixed single section. Either
    way the page works from the same `sections` list of { key, label }, where a
    tab's key is the sectionKey its items are saved under.
  */
  const isDynamic = !!moduleDef.dynamicTabs;
  const [tabs, setTabs] = useState([]);
  const sections = isDynamic
    ? tabs.map((tab) => ({ key: tab.tabKey, label: tabLabel(tab) }))
    : moduleDef.sections;
  const [activeSection, setActiveSection] = useState(isDynamic ? null : moduleDef.sections[0].key);
  const [list, setList] = useState([]);
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isManageTabs, setIsManageTabs] = useState(false);

  const doSearch = async (section) => {
    try {
      dispatch(appLoaderAction.enable());
      setList(await intranetService.searchBySection(section));
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  /**
   * NMU-601: (re)load the tabs, then open one.
   *
   * @param preferKey the tab to land on — the one just saved, or the one that
   *   was open. When it no longer exists (it was just deleted) the first tab
   *   opens instead, and with no tabs at all there is nothing to list.
   */
  const loadTabs = async (preferKey) => {
    let loaded = [];
    try {
      dispatch(appLoaderAction.enable());
      loaded = await intranetService.listTabs();
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
    setTabs(loaded);

    const keys = loaded.map((tab) => tab.tabKey);
    const next = keys.includes(preferKey) ? preferKey : keys[0] || null;
    setActiveSection(next);
    if (next) {
      await doSearch(next);
    } else {
      setList([]);
    }
  };

  useEffect(() => {
    // Reset to the first section whenever the sidebar switches sub-module.
    const def = INTRANET_MODULES[moduleKey];
    if (def.dynamicTabs) {
      loadTabs(null);
      return;
    }
    const first = def.sections[0].key;
    setActiveSection(first);
    doSearch(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey]);

  const onDelete = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      await intranetService.remove(id);
      notifyUtitls.success("สำเร็จ", "ลบข้อมูลเรียบร้อย");
      await doSearch(activeSection);
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  /** Move a card and persist the resulting order for this section only. */
  const onMove = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setList(reordered);
    try {
      dispatch(appLoaderAction.enable());
      await intranetService.reorder(activeSection, reordered.map((r) => r.intranetCardId));
      await doSearch(activeSection);
    } catch (err) {
      notifyUtitls.error(err);
      await doSearch(activeSection); // fall back to the saved order
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  /*
    NMU-580: this table shows วันที่/เวลาสร้าง but does not sort by it.

    The row order here *is* the data — ลำดับ carries the move buttons and
    saving writes the sequence the rows are currently in. Re-ordering them by
    creation date would leave those buttons moving a การ์ด against a
    sequence nobody chose, and the next save would write that back over the
    arrangement. Flagged on the ticket.
  */
  const columns = [
    {
      title: "ลำดับ",
      key: "order",
      width: 130,
      render: (_, row, index) => (
        <div className="d-flex align-items-center">
          <span className="me-2">{row.seqNo || index + 1}</span>
          <Button size="small" className="me-1" disabled={index === 0} onClick={() => onMove(index, -1)}>
            <Icon.ChevronUp size={14} />
          </Button>
          <Button size="small" disabled={index === list.length - 1} onClick={() => onMove(index, 1)}>
            <Icon.ChevronDown size={14} />
          </Button>
        </div>
      ),
    },
    {
      // NMU-489: the glyph, not the key — "folder" told an editor nothing about
      // what the card would look like on the portal.
      title: "ไอคอน",
      dataIndex: "icon",
      key: "icon",
      width: 130,
      align: "center",
      render: (icon) => <IntranetIcon value={icon} />,
    },
    {
      title: "ชื่อรายการ",
      key: "title",
      render: (_, row) => (row.title && (row.title.titleTH || row.title.titleEN)) || "-",
    },
    { title: "ป้ายกำกับ", dataIndex: "badgeTH", key: "badgeTH", width: 150 },
    {
      title: "ปลายทาง",
      key: "target",
      render: (_, row) =>
        row.linkType === LINK_TYPE.CONTENT ? `เนื้อหา: ${row.templateId || "-"}` : row.url || "-",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (status ? <Tag color="green">Online</Tag> : <Tag>Offline</Tag>),
    },
    {
      /*
        NMU-600: pinned to the right edge, as it is in every other backoffice
        table (see withCreatedColumn). It was the one table without that, and
        with eight columns in a card beside the sidebar the row outgrew the card
        on anything narrower than ~1300px — ดำเนินการ was clipped off the right
        edge with no scrollbar, so Edit and Delete existed but could not be
        reached. The `scroll` on the Table below is the other half: pinning lays
        this column over the ones to its left, and without a scrolling table
        body วันที่/เวลาสร้าง would sit under it for good.
      */
      title: "ดำเนินการ",
      key: "action",
      width: 130,
      fixed: "right",
      render: (_, row) => (
        <div className="d-flex">
          <label
            className="pe-3 border-end"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setEditData(row);
              setIsShowDialog(true);
            }}
          >
            <Icon.Edit size={18} />
          </label>
          <Popconfirm title="ยืนยันการลบข้อมูล ?" onConfirm={() => onDelete(row.intranetCardId)}>
            <label className="ps-3" style={{ cursor: "pointer" }}>
              <Icon.Trash2 size={18} />
            </label>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const table = (
    <>
      <div className="d-flex justify-content-between mb-3">
        <Button
          type="primary"
          onClick={() => {
            setEditData(null);
            setIsShowDialog(true);
          }}
        >
          <Icon.Plus className="me-1" size={18} /> เพิ่มรายการ
        </Button>
      </div>
      <Table
        rowKey={(row) => row.intranetCardId}
        columns={withCreatedColumn(columns, createdColumnStatic)}
        dataSource={list}
        pagination={false}
        scroll={{ x: true }}
      />
    </>
  );

  const manageTabsButton = (
    <Button onClick={() => setIsManageTabs(true)}>
      <Icon.Settings className="me-1" size={16} /> จัดการแท็บ
    </Button>
  );

  return (
    <div>
      <h2 className="color-primary-title">{moduleDef.titleTH}</h2>
      <Breadcrumb>
        <Breadcrumb.Item>Intranet Management</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleDef.title}</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />

      <Card bordered={false}>
        {isDynamic && sections.length === 0 ? (
          <Empty
            className="my-4"
            description="ยังไม่มีแท็บ — กด “จัดการแท็บ” เพื่อเพิ่มแท็บแรก"
          >
            {manageTabsButton}
          </Empty>
        ) : isDynamic || sections.length > 1 ? (
          // A managed tab shows as a tab even when it is the only one, so the
          // editor can see which tab an item is going into.
          <Tabs
            activeKey={activeSection}
            tabBarExtraContent={isDynamic ? manageTabsButton : null}
            onChange={(key) => {
              setActiveSection(key);
              doSearch(key);
            }}
          >
            {sections.map((section) => (
              <Tabs.TabPane tab={section.label} key={section.key}>
                {table}
              </Tabs.TabPane>
            ))}
          </Tabs>
        ) : (
          table
        )}
      </Card>

      {isDynamic && (
        <ManageWorkspaceTabsDialog
          isShow={isManageTabs}
          tabs={tabs}
          onCancel={() => setIsManageTabs(false)}
          onChanged={(savedKey) => loadTabs(savedKey || activeSection)}
        />
      )}

      <AddEditIntranetCardDialog
        isShow={isShowDialog}
        sectionKey={activeSection}
        editData={editData}
        onCancel={() => setIsShowDialog(false)}
        onSaveComplete={() => {
          setIsShowDialog(false);
          doSearch(activeSection);
        }}
      />
    </div>
  );
};

export default IntranetManagement;
